import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import clsx from "clsx";
import Tooltip from "./Tooltip";
import {removeSpaces} from "./functions";


function descendingComparator(a, b, orderBy) {
    let ca, cb
    const f = /^[+-]?\d+(\.\d+)?$/
    if (typeof a[orderBy] === "object") {
        if (a[orderBy].time === "(0)" || a[orderBy].time === "--" || a[orderBy].time === "U" || a[orderBy].time == null || a[orderBy].time === "UNSAT")
            return -1
        if (typeof a[orderBy].time === "number")
            ca = a[orderBy].time
        else {
            if(a[orderBy].time.indexOf('(') == -1) {
             ca = parseFloat(removeSpaces(a[orderBy].time))
            } else {
                let tmp = a[orderBy].time.split('(')
                ca = tmp.length === 0 ? a[orderBy].time : parseFloat(tmp[1].split(')')[0])
            }
        }
    } else {
        //console.log(a[orderBy], typeof a[orderBy], orderBy)
        if (a[orderBy] === undefined)
            return -1
        if (typeof a[orderBy] === "string") {
            let tmp = removeSpaces(a[orderBy])
            if (tmp.match(f) !== null)
                ca = parseFloat(tmp)
            else
                ca = a[orderBy].toUpperCase()
        } else
            ca = a[orderBy]
    }


    if (typeof a[orderBy] === "object") {
        if (b[orderBy].time === "(0)" || b[orderBy].time === "--" || b[orderBy].time === "U" || b[orderBy].time == null || b[orderBy].time === "UNSAT")
            return +1
        if (typeof b[orderBy].time === "number")
            cb = b[orderBy].time
        else {
            if(b[orderBy].time.indexOf('(') == -1) {
                cb = parseFloat(removeSpaces(b[orderBy].time))
            } else {
                let tmp = b[orderBy].time.split('(')
                cb = tmp.length === 0 ? b[orderBy].time : parseFloat(tmp[1].split(')')[0])
            }
        }

    } else {
        if (b[orderBy] === undefined)
            return 1
        if (typeof b[orderBy] === "string") {
            let tmp = removeSpaces(b[orderBy])
            if (tmp.match(f) !== null)
                cb = parseFloat(tmp)
            else
                cb = b[orderBy].toUpperCase()

        } else
            cb = b[orderBy]
    }


    if (cb < ca)
        return -1
    if (cb > ca)
        return 1
    if(a['solver'] != undefined && a['solver'] == "<i>Virtual Best Solver</i>")
        return -1
    if(b['solver'] != undefined && b['solver'] == "<i>Virtual Best Solver</i>")
        return 1
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}


function EnhancedTableHead(props) {
    const {order, orderBy, onRequestSort} = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {props.headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        className={clsx(props.headerClasses !== undefined && props.headerClasses.hd)}
                        style={{backgroundColor: "lightgrey", paddingLeft:"10px"}}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    headCells: PropTypes.array.isRequired,
    headerClasses: PropTypes.object
};

const useStyles = makeStyles((theme) => ({
    table: {
        minWidth: 750,
        position: "relative"
    },
    best: {
        color: "#4a9f38",
        fontWeight: "bold"
    },
    conflict: {
        backgroundColor: "#ffe7e4",
    },
    optim: {
        color: "#4a9f38",
        fontWeight: "bold"
    },
    bad: {
        color: "#777",
    },
    vbs : {
        backgroundColor: "#cdfbbc"
    },
    th: {
        backgroundColor: "lightgrey"
    }
}));

export default function SortedTable(props) {
    const classes = useStyles();
    const [order, setOrder] = React.useState(props.asc === undefined ? 'asc' : props.asc);
    const [orderBy, setOrderBy] = React.useState(props.order === undefined ? 'id' : props.order);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    let rows = props.rows;
    const headCells = props.headCells;
    let allrows = rows
    if (props.tooltip) { // Need to remove id when display lines
        allrows = rows.map(({id, info_domains, info_constraints, useless_vars, ...item}) => item)
    }

    let sz  =  allrows.length == 0 ? 100 : 100/Object.keys(allrows[0]).length
    return (
        <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table" size="small">
            <EnhancedTableHead
                classes={classes}
                headerClasses={props.headerClasses}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                headCells={headCells}
            />
            <TableBody>
                {stableSort(rows, getComparator(order, orderBy))
                    .map((row, index) => {
                        return (
                            <TableRow hover tabIndex={-1} key={row.name + "-" + index}>
                                {Object.keys(allrows[index]).map((e, i) => {

                                        if (typeof row[e] == "object") {
                                            return (
                                                <TableCell
                                                    onClick={e => i === 0 || props.handleClick === undefined ? {} : props.handleClick(e, row.id)}
                                                    className={row[e].classes === null ? null : classes[row[e].classes]}
                                                    key={i}
                                                    style={{minWidth: sz + "%"}}
                                                    align="right"
                                                    dangerouslySetInnerHTML={{__html: row[e].time}}></TableCell>
                                            )
                                        }
                                        if(i === 0 && props.tooltip ) {
                                            return (
                                                <TableCell
                                                    onClick={e => i === 0 || props.handleClick === undefined ? {} : props.handleClick(e, row.id)}
                                                    align={headCells[i] !== undefined && headCells[i].numeric ? "right" : "left"}
                                                    key={"tr" + i}
                                                    style={{minWidth: sz+"%"}}>
                                                     <Tooltip
                                                        data={"<b>" + row.name +
                                                            "</b><br>&nbsp;&nbsp;&nbsp;#vars:" +
                                                            row.nb_variables + (row.useless_vars == 0 ? "" : " (#useless:" + row.useless_vars +")") +
                                                            " #ctrs:" + (row.nb_constraints === undefined ? row.nb_clauses : row.nb_constraints) +
                                                            "<br>&nbsp;&nbsp;&nbsp;Domains&rarr;" + row.info_domains
                                                            + "<br>&nbsp;&nbsp;&nbsp;Constraints&rarr;" + row.info_constraints +
                                                            (row.bound === undefined ? "" : "<br>&nbsp;&nbsp;&nbsp;Optimization&rarr;" + row.type) }>{row[e]}

                                                    </Tooltip>
                                                </TableCell>
                                            )
                                        }
                                        if(e == "classes" || (props.removeCells !== undefined && props.removeCells.includes(e)))
                                            return null
                                        return (
                                            <TableCell
                                                style={{minWidth: sz+"%"}}
                                                className={row.classes === null ? null : classes[row.classes]}
                                                onClick={e => i === 0 || props.handleClick === undefined ? {} : props.handleClick(e, row.id)}
                                                align={headCells[i] !== undefined && headCells[i].numeric ? "right" : "left"}
                                                key={"tr" + i} dangerouslySetInnerHTML={{__html: row[e]}}></TableCell>

                                        )
                                    }
                                )}
                            </TableRow>
                        );
                    })}
            </TableBody>
        </Table>
    );
}